import { Engineer } from "resource-planner-model";
import env from "../app/Environment";
import fetchService from "../utils/FetchService";
import {
  fetchAllList,
  fetchListPage,
  PaginatedListRequest,
  PaginatedListResponse
} from "../utils/PaginatedListService";
import { PersistEngineerResult } from "./EngineersTypes";

export interface EngineerService {
  fetch(): Promise<Engineer[]>;
  fetchPage(request?: PaginatedListRequest): Promise<PaginatedListResponse<Engineer>>;
  delete(id: string): Promise<void>;
  persist(item: Engineer): Promise<PersistEngineerResult>;
  setJiraAccess(id: string): Promise<void>;
  setConfluenceAccess(id: string): Promise<void>;
}

class HTTPEngineerService implements EngineerService {
  constructor(private endpoint: string) {}

  async fetch(): Promise<Engineer[]> {
    return fetchAllList<Engineer>(`${this.endpoint}/engineers`, (url) =>
      fetchService.fetch(url).then((r) => r.json())
    ).catch((): any[] => []);
  }

  async fetchPage(
    request: PaginatedListRequest = {}
  ): Promise<PaginatedListResponse<Engineer>> {
    return fetchListPage<Engineer>(
      `${this.endpoint}/engineers`,
      (url) => fetchService.fetch(url).then((r) => r.json()),
      request
    ).catch(() => ({ items: [] as Engineer[] }));
  }

  async delete(id: string): Promise<void> {
    return fetchService
      .fetch(`${this.endpoint}/engineers/${id}`, {
        method: "DELETE",
      })
      .then();
  }

  async persist(item: Engineer): Promise<PersistEngineerResult> {
    return fetchService
      .fetch(`${this.endpoint}/engineers`, {
        method: "POST",
        body: JSON.stringify(item),
      })
      .then((r) => r.json());
  }

  async setJiraAccess(id: string): Promise<void> {
    return fetchService
      .fetch(`${this.endpoint}/engineers/${id}/jira/access`, {
        method: "POST",
      })
      .then(async (response) => {
        if (response.status && response.status !== 200) {
          throw new Error(`failed to grant Jira access`);
        }
      });
  }

  async setConfluenceAccess(id: string): Promise<void> {
    return fetchService
      .fetch(`${this.endpoint}/engineers/${id}/confluence/access`, {
        method: "POST",
      })
      .then(async (response) => {
        if (response.status && response.status !== 200) {
          throw new Error(`failed to grant Confluence access`);
        }
      });
  }
}

export function sortEngineers(e1: Engineer, e2: Engineer): number {
  const str1 = `${e1.lastName} ${e1.firstName}`;
  const str2 = `${e2.lastName} ${e2.firstName}`;
  return str1.localeCompare(str2);
}

export function isEngineerInTeam(e: Engineer, teamId: string): boolean {
  return new Set(e.utilization.map((u) => u.team)).has(teamId);
}

const service: EngineerService = new HTTPEngineerService(env.endpoint);

export default service;
